import React from 'react';
import { ToggleButtonGroup, ToggleButton, Chip } from '@mui/material';
import { TaskDepartment, TASK_DEPARTMENT_LABELS, TASK_DEPARTMENT_VALUES } from '../../constants/taskDepartments';
import type { TaskQueueCounts } from '../../services/taskQueueService';

export type TaskQueueTab = 'all' | TaskDepartment | 'mine';

export interface TaskQueueTabsProps {
  /** Undefined while the counts query is loading — every chip shows 0 rather than nothing. */
  counts: TaskQueueCounts | undefined;
  value: TaskQueueTab;
  onChange: (value: TaskQueueTab) => void;
}

/**
 * PRX-67 — All / Billing / Clinical / Logistics / Blister pack / Mine tabs with
 * live counts (R4, R5), in the shape of the Communication Center's
 * `ToggleButtonGroup` (`CommunicationCenter.tsx:749-797`).
 *
 * Pulled out of `TaskQueueContent` as its own component (rather than inlined
 * in that already-2000+-line file) so this piece's behaviour — which tab is
 * active, which count shows on which chip — has its own light spec instead of
 * requiring the whole page to mount.
 */
// PRX-67 (wave 1 review item 3, Medium, ruling) — every chip is an OPEN count
// (R7: getTaskQueueCounts scopes to OPEN_STATUSES), but nothing on the tab
// said so — a reader could take "All: 12" as the list's total row count,
// which can differ (R6a: raw troubleshooting-queue items carry no department
// and are never counted here). title + aria-label spell this out; the list's
// own default status filter is unchanged by this.
const tabAccessibleLabel = (label: string, count: number): string => `${label}: ${count} open`;

export const TaskQueueTabs: React.FC<TaskQueueTabsProps> = ({ counts, value, onChange }) => {
  const allCount = counts?.all ?? 0;
  const mineCount = counts?.mine ?? 0;

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, next) => next && onChange(next)}
      size="small"
      sx={{ flexWrap: 'wrap' }}
    >
      <ToggleButton value="all" title={tabAccessibleLabel('All', allCount)} aria-label={tabAccessibleLabel('All', allCount)}>
        All
        <Chip label={allCount} size="small" sx={{ ml: 1, height: 20, minWidth: 20, px: 0.5 }} />
      </ToggleButton>
      {TASK_DEPARTMENT_VALUES.map((department) => {
        const label = TASK_DEPARTMENT_LABELS[department];
        const count = counts?.byDepartment?.[department] ?? 0;
        return (
          <ToggleButton
            key={department}
            value={department}
            title={tabAccessibleLabel(label, count)}
            aria-label={tabAccessibleLabel(label, count)}
          >
            {label}
            <Chip label={count} size="small" sx={{ ml: 1, height: 20, minWidth: 20, px: 0.5 }} />
          </ToggleButton>
        );
      })}
      <ToggleButton value="mine" title={tabAccessibleLabel('Mine', mineCount)} aria-label={tabAccessibleLabel('Mine', mineCount)}>
        Mine
        <Chip label={mineCount} size="small" sx={{ ml: 1, height: 20, minWidth: 20, px: 0.5 }} />
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default TaskQueueTabs;
